import { IonBadge, IonLabel, IonList, IonListHeader } from '@ionic/react';
import { sortBy } from 'lodash';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EventDefinition,
  useListEventDefinitionsQuery,
} from '../../api/graphql/generated';
import { useBeaconRouter } from '../../route/route.hooks';
import { LoadingRetry } from '../graphql/LoadingRetry';
import { IonItem } from '../ionic/IonItem';

interface EventListProps {
  teamId: string;
  machineId: string;
}

const EventList = (props: EventListProps): ReactElement => {
  const { teamId, machineId } = props;
  const { i18n } = useTranslation();

  const {
    data: eventDefinitionsData,
    loading,
    error,
    refetch,
  } = useListEventDefinitionsQuery({
    variables: {
      input: {
        parentId: machineId,
      },
    },
    fetchPolicy: 'network-only',
  });

  const events = (eventDefinitionsData?.listEventDefinitions.items ||
    []) as EventDefinition[];

  const systemEvents: EventDefinition[] = [];
  const customEvents: EventDefinition[] = [];

  for (const evt in events) {
    if (events[evt].builtin === true) {
      systemEvents.push(events[evt]);
    } else {
      customEvents.push(events[evt]);
    }
  }

  return (
    <LoadingRetry loading={loading} error={error} refetch={refetch}>
      <EventSection
        label={i18n.t('common:event_custom')}
        events={customEvents}
        teamId={teamId}
        machineId={machineId}
      />
      <EventSection
        label={i18n.t('common:event_system')}
        events={systemEvents}
        teamId={teamId}
        machineId={machineId}
      />
    </LoadingRetry>
  );
};

interface EventSectionProps {
  label: string;
  events: EventDefinition[];
  teamId: string;
  machineId: string;
}

const EventSection = (props: EventSectionProps): ReactElement | null => {
  const { events, label, teamId, machineId } = props;
  const sortedEvents = sortBy(events, ['name']);

  if (sortedEvents.length !== 0) {
    return (
      <>
        <IonListHeader>
          <IonLabel>{label}</IonLabel>
        </IonListHeader>
        <IonList lines="full">
          {sortedEvents.map((e) => {
            return (
              <EventItem
                key={e.id}
                eventDetail={e}
                teamId={teamId}
                machineId={machineId}
              />
            );
          })}
        </IonList>
      </>
    );
  } else {
    return null;
  }
};

interface EventItemProps {
  eventDetail: EventDefinition;
  teamId: string;
  machineId: string;
}

const EventItem = (props: EventItemProps): ReactElement => {
  const { eventDetail, teamId, machineId } = props;
  const { label } = eventDetail;
  const subscriptionCount = eventDetail.subscriptions?.length || 0;

  const { builder } = useBeaconRouter();

  return (
    <IonItem
      routerLink={builder.getMachineEventDefinitionRoute(
        teamId,
        machineId,
        eventDetail.id
      )}
    >
      <IonLabel>{label}</IonLabel>
      {subscriptionCount > 0 && (
        <IonBadge color="success">{subscriptionCount}</IonBadge>
      )}
    </IonItem>
  );
};

export { EventList };
